import { apiFetch, authHeader, jsonBody } from '../../shared/api/httpClient';
import { clientHeaders } from '../../shared/lib/device';
import { raffleResultPath } from '../../shared/lib/raffleResultTarget';
import { activeCountry } from '../../shared/country/countryStore';

export type WalletPaymentChannel = {
  requiresPayerIdentity?: boolean;
  requiresPayoutDocument?: boolean;
  channelId: string;
  label?: string;
  direction: 'deposit' | 'withdraw' | string;
  asset?: string;
  network?: string;
  status?: string;
  quickAmounts?: number[];
  amountPrecision?: number;
  amountStep?: number;
  allowCustomAmount?: boolean;
  payoutStep?: number | null;
  dailyRemaining?: number | null;
  depositOffers?: Array<{id:number;amount:number;bonusFixed:number;bonusPercent:number}>;
  minAmount?: number;
  maxAmount?: number;
  dailyLimit?: number;
  feeFlat?: number;
  feePct?: number;
  health?: string;
  checkoutMode?: 'redirect' | 'iframe' | string;
};

export type WalletCashierSettings = Pick<WalletPaymentChannel, 'asset' | 'quickAmounts' | 'amountPrecision' | 'amountStep' | 'allowCustomAmount' | 'payoutStep' | 'dailyRemaining' | 'depositOffers' | 'minAmount' | 'maxAmount' | 'dailyLimit' | 'feeFlat' | 'feePct'>;
type CashierResponse = { deposit: WalletCashierSettings; withdraw: WalletCashierSettings };

export type WalletOrder = {
  id: number | string;
  publicId?: string;
  channelId?: string;
  channelLabel?: string;
  asset?: string;
  network?: string;
  requestedAmount?: number;
  receivedAmount?: number;
  feeAmount?: number;
  payoutAmount?: number;
  status?: string;
  providerRef?: string;
  providerOrderId?: string;
  psp_order_id?: string;
  checkoutUrl?: string;
  checkoutMode?: 'redirect' | 'iframe' | string;
  checkoutSandboxFallback?: boolean;
  createdAt?: number;
  updatedAt?: number;
  paidAt?: number;
  expiresAt?: number;
  /** Bonus settled with this deposit (offer + paid coupon); 0 when none. */
  bonusAmount?: number;
};

export type WalletStatementItem = {
  id: number | string;
  betId?: number | string | null;
  depositId?: number | string | null;
  withdrawId?: number | string | null;
  // The reference a player can quote to support (PIX-…): the numeric ids above
  // are global sequences and must not be printed. Absent until the backend
  // carrying them ships, so callers fall back rather than invent one.
  depositPublicId?: string | null;
  withdrawPublicId?: string | null;
  category?: string;
  currency?: string;
  asset?: string;
  network?: string;
  amount?: number;
  balanceAfter?: number;
  balance_after?: number;
  createdAt?: number;
  created_at?: number;
};

export type WalletData = {
  cashierUnavailable?: boolean;
  depositSettings?: WalletCashierSettings;
  withdrawSettings?: WalletCashierSettings;
  depositChannels: WalletPaymentChannel[];
  withdrawChannels: WalletPaymentChannel[];
  deposits: WalletOrder[];
  withdrawals: WalletOrder[];
  statement: WalletStatementItem[];
};

export type NextUnlock = {
  wagerRemaining: number;
  wagerRequired: number;
  unlockAmount: number;
  kind: 'principal' | 'bonus';
};

export type BonusAuditStatus = {
  asset?: string;
  totalBalance?: number;
  withdrawableBalance?: number;
  lockedBalance?: number;
  auditPolicy?: string;
  activeAuditRequired?: number;
  principalBalance: number;
  bonusBalance: number;
  bonusFrozenAmount?: number;
  principalRemaining: number;
  bonusRemaining: number;
  principalWithdrawable: boolean;
  abandonmentRequired: boolean;
  enrollmentId?: number | null;
  /** Next FIFO milestone; null when no audit is active. Older backends omit it. */
  nextUnlock?: NextUnlock | null;
};
export type BonusAuditRecord = { terminationReason?: string; withdrawalBonusStatus?:'held'|'released'|'forfeited'; id:number; kind:'principal'|'bonus'|'combined'; sourceLabel:string; sourceDepositId?:number; principalLotId?:number; status:string; principalRequired:number; principalRemaining:number; bonusGranted:number; bonusRequired:number; bonusRemaining:number; availableAmount?:number; linkedBonusAmount?:number; enrolledAt:number };
export type WithdrawalEligibility = {
  canWithdraw: boolean;
  maxWithdrawableAmount: number;
  requestedAmountEligible: boolean;
  requiresBonusAbandonment: boolean;
  forfeitedBonusAmount: number;
  quoteToken: string;
  bonusSources: { principalLotId: number; bonusAmount: number }[];
  walletBalanceAfterSuccess: number;
  hasPendingBonusSources: boolean;
};

export type RaffleEvent = {
  event_id?: number | string;
  id?: number | string;
  display_name?: string;
  status?: string;
  scheduled_at?: number;
  total_entries?: number;
  /** Ticket numbering origin: 0 for current rounds, 1 for preserved legacy rounds. */
  serial_base?: number;
  min_entries_to_run?: number;
  min_unique_users_to_run?: number;
  max_total_entries?: number;
  max_entries_per_user?: number;
  anchor_round_number?: number | string;
  d_digits?: string;
  winners?: RaffleWinner[];
  prize_brackets?: Array<{
    min_n?: number;
    max_n?: number;
    pool?: number;
    tiers?: Array<{
      rank?: number;
      count?: number;
      amount?: number;
      rewards?: Array<{
        kind?: 'cash' | 'raffle_ticket' | 'coupon' | string;
        amount?: number;
        ticket_count?: number;
        coupon_template_id?: number;
      }>;
    }>;
  }>;
};

export type RaffleTicket = {
  ticket_id?: number | string;
  id?: number | string;
  user_id?: number | string;
  status?: 'active' | 'consumed' | 'refunded' | 'expired' | string;
  source?: string;
  issued_at?: number;
  expires_at?: number;
};

export type RaffleRecentEvent = {
  event_id?: number | string;
  id?: number | string;
  display_name?: string;
  scheduled_at?: number;
  total_entries?: number;
  /** Ticket numbering origin: 0 for current rounds, 1 for preserved legacy rounds. */
  serial_base?: number;
  max_total_entries?: number;
  anchor_round_number?: number | string;
  d_digits?: string;
  /** 可为 null —— 取值处必须在 `Number()` 之前挡掉，`Number(null)` 是 0 不是 NaN。 */
  my_prize?: number | null;
  status?: string;
  min_entries_to_run?: number;
  winners?: RaffleWinner[];
  my_entries?: RaffleEntry[];
  // Full public entry list (serial → @username) — the "N" of (D mod N) + 1, so
  // the draw can be recomputed from what's on screen.
  entries?: RaffleEntry[];
  /**
   * 奖级配置。**已结算的期不要用 `poolOf(prize_brackets)` 算池额** —— 那个
   * 函数只读 `brackets[0]`，只对*未开奖*的期成立（N 还不知道，`brackets[0]`
   * 读作起始池额是对的）。一个已结算的期按 `total_entries` 落进三档里的一
   * 档（`JdbcRaffleRepository.appliedBracket`），真正付出去的钱用
   * `settledPoolOf(winners, prize_brackets, total_entries)` 算 —— 优先对
   * `winners[].amount` 求和，没有中奖记录时才退回按 `total_entries` 选档。
   */
  prize_brackets?: RaffleEvent['prize_brackets'];
  /** `status === 'voided'` 时的作废原因，通常是未达最低参与人数。 */
  voided_reason?: string;
};

export type RaffleData = {
  event?: RaffleEvent | null;
  tickets?: RaffleTicket[];
  entries?: RaffleEntry[];
  recent_events?: RaffleRecentEvent[];
  ticket_rules?: unknown[];
  server_time?: number | { unix?: number };
};

export type RaffleEntry = {
  entry_id?: number | string;
  event_id?: number | string;
  user_id?: number | string;
  username?: string;
  avatar_default_id?: number;
  avatar_thumb?: string;
  ticket_id?: number | string;
  serial_no?: number;
  registered_at?: number;
};

export type RaffleWinner = {
  winner_id?: number | string;
  event_id?: number | string;
  entry_id?: number | string;
  user_id?: number | string;
  username?: string;
  avatar_default_id?: number;
  avatar_thumb?: string;
  serial_no?: number;
  rank?: number;
  amount?: number;
};

type ItemsResponse<T> = {
  items?: T[];
};

type WalletOrderResponse = {
  item?: WalletOrder;
};

// Read callback-confirmed state without querying or simulating the provider.
export async function fetchRecentDeposits(token: string): Promise<WalletOrder[]> {
  const response = await apiFetch<ItemsResponse<WalletOrder>>('/api/wallet/deposits?limit=10', {
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
  return response.items || [];
}

/** Newest deposit orders, enough to cover the 72-hour unpaid window for any realistic player. */
export async function fetchDepositOrders(token: string, limit = 50): Promise<WalletOrder[]> {
  const response = await apiFetch<ItemsResponse<WalletOrder>>(`/api/wallet/deposits?limit=${limit}`, {
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
  return response.items || [];
}

export async function fetchWalletData(token: string, options: { fullOrderHistory?: boolean } = {}): Promise<WalletData> {
  const headers = {
    ...clientHeaders(),
    ...authHeader(token),
  };
  const depositsRequest = options.fullOrderHistory
    ? fetchAllWalletOrders('/api/wallet/deposits', headers)
    : apiFetch<ItemsResponse<WalletOrder>>('/api/wallet/deposits?limit=50', { headers });
  const withdrawalsRequest = options.fullOrderHistory
    ? fetchAllWalletOrders('/api/wallet/withdrawals', headers)
    : apiFetch<ItemsResponse<WalletOrder>>('/api/wallet/withdrawals?limit=50', { headers });
  // Independent endpoints — degrade each on its own instead of all-or-nothing.
  // A transient blip on a secondary call (channels/orders) must not blank the
  // statement, which is the page's primary content. Only a statement failure is
  // surfaced as an error (so the user gets Retry, not a misleading empty ledger).
  const fiatCurrency = activeCountry().fiatCurrency;
  const [depositChannels, withdrawChannels, deposits, withdrawals, statement, cashier] = await Promise.allSettled([
    apiFetch<ItemsResponse<WalletPaymentChannel>>('/api/wallet/payment-channels?direction=deposit', { headers }),
    apiFetch<ItemsResponse<WalletPaymentChannel>>('/api/wallet/payment-channels?direction=withdraw', { headers }),
    depositsRequest,
    withdrawalsRequest,
    apiFetch<ItemsResponse<WalletStatementItem>>(`/api/wallet/statement?asset=${encodeURIComponent(fiatCurrency)}&limit=100`, { headers }),
    apiFetch<CashierResponse>('/api/wallet/cashier-settings', { headers }),
  ]);
  if (statement.status === 'rejected') throw statement.reason;

  const itemsOf = <T,>(result: PromiseSettledResult<ItemsResponse<T>>): T[] =>
    result.status === 'fulfilled' ? result.value.items || [] : [];

  return {
    cashierUnavailable: cashier.status === 'rejected',
    depositSettings: cashier.status === 'fulfilled' ? cashier.value.deposit : undefined,
    withdrawSettings: cashier.status === 'fulfilled' ? cashier.value.withdraw : undefined,
    depositChannels: itemsOf(depositChannels),
    withdrawChannels: itemsOf(withdrawChannels),
    deposits: itemsOf(deposits),
    withdrawals: itemsOf(withdrawals),
    statement: statement.value.items || [],
  };
}

/** The statement rows older than `before` (an id from the previous page), newest first. */
export function fetchWalletStatementBefore(token: string, fiatCurrency: string, before: string, limit = 100) {
  return apiFetch<ItemsResponse<WalletStatementItem>>(
    `/api/wallet/statement?asset=${encodeURIComponent(fiatCurrency)}&limit=${limit}&before=${encodeURIComponent(before)}`,
    { headers: { ...clientHeaders(), ...authHeader(token) } },
  );
}

async function fetchAllWalletOrders(path: string, headers: HeadersInit): Promise<ItemsResponse<WalletOrder>> {
  const pageSize = 50;
  const all: WalletOrder[] = [];
  let previousFirstId = '';

  for (let offset = 0; ; offset += pageSize) {
    const page = await apiFetch<ItemsResponse<WalletOrder>>(`${path}?limit=${pageSize}&offset=${offset}`, { headers });
    const items = page.items || [];
    const firstId = String(items[0]?.publicId ?? items[0]?.id ?? '');
    // During a rolling deployment an older API may ignore `offset`. Keep the
    // first page instead of looping forever; the next refetch will use paging.
    if (offset > 0 && firstId && firstId === previousFirstId) break;
    all.push(...items);
    if (items.length < pageSize) break;
    previousFirstId = firstId;
  }

  return { items: all };
}

export function fetchRaffleData(token: string) {
  return apiFetch<RaffleData>('/api/raffle', {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    cache: 'no-store',
  });
}

export function fetchRaffleResult(token: string, eventId: string) {
  return apiFetch<RaffleRecentEvent>(raffleResultPath(eventId), {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    cache: 'no-store',
  });
}

export type RaffleScheduleDraw = {
  event_id?: string | null;
  scheduled_at: number;
  registration_starts: number;
  status: string;
  pool?: number;
  currency?: string;
  total_entries?: number | null;
  materialized?: boolean;
};

export type RaffleScheduleData = {
  server_time?: number;
  draws?: RaffleScheduleDraw[];
};

export function fetchRaffleSchedule(token: string) {
  return apiFetch<RaffleScheduleData>('/api/raffle/schedule', {
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    cache: 'no-store',
  });
}

export function enterRaffleEvent(token: string, eventId: string | number, count: number) {
  return apiFetch<{ event?: RaffleEvent; entries?: RaffleEntry[] }>(`/api/raffle/events/${encodeURIComponent(String(eventId))}/entries`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody({ count }),
  });
}

export function createWalletDeposit(token: string, payload: { channelId: string; amount: number; cpf?: string; name?: string; selectedCouponId?: number | null; selectedOfferId?: number | null }) {
  return apiFetch<WalletOrderResponse>('/api/wallet/deposits', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

/**
 * Reconcile one pending provider deposit before reading its current order state.
 * This calls the server-side provider query endpoint; it is not a wallet credit
 * operation and remains idempotent when the provider callback arrived first.
 */
export function syncWalletDeposit(token: string, publicId: string) {
  return apiFetch<WalletOrderResponse>(`/api/wallet/deposits/${encodeURIComponent(publicId)}/sync`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

/** Marks that the player returned from the provider checkout before polling it. */
export function markWalletDepositCheckoutReturned(token: string, publicId: string) {
  return apiFetch<WalletOrderResponse>(`/api/wallet/deposits/${encodeURIComponent(publicId)}/checkout-returned`, {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
  });
}

// ---- Deposit coupons (subsystem G) ----
export type DepositCoupon = {
  id: number;
  templateId: number;
  status: 'available' | 'locked' | 'used' | 'expired' | string;
  expiresAt?: number | null;
  code: string;
  nameI18n?: Record<string, string>;
  descI18n?: Record<string, string>;
  bonusType: 'percent' | 'fixed' | string;
  bonusValue: number;
  minDeposit: number;
  maxBonus: number;
  auditMultiplier: number;
  principalAuditMultiplier: number;
  bonusAuditMultiplier: number;
  estimatedBonus?: number;
  grantSource?: string;
  sourceRef?: string;
};
export type ClaimableCoupon = {
  templateId: number;
  code: string;
  nameI18n?: Record<string, string>;
  descI18n?: Record<string, string>;
  bonusType: 'percent' | 'fixed' | string;
  bonusValue: number;
  minDeposit: number;
  maxBonus: number;
  auditMultiplier: number;
  principalAuditMultiplier: number;
  bonusAuditMultiplier: number;
  validityDays: number;
};

export function fetchDepositCouponsForAmount(token: string, amount: number) {
  return apiFetch<{ items?: DepositCoupon[] }>(`/api/deposit/coupons?amount=${encodeURIComponent(String(amount || 0))}`, {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}

export function fetchMyCoupons(token: string) {
  return apiFetch<{ items?: DepositCoupon[] }>('/api/deposit/coupons/mine', {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}

export function fetchClaimableCoupons(token: string) {
  return apiFetch<{ items?: ClaimableCoupon[] }>('/api/deposit/coupons/claimable', {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}

export type CouponHistoryItem = DepositCoupon & {
  usedAt?: number | null;
  usedDepositId?: number | null;
  depositAmount?: number | null;
  bonusEarned?: number | null;
};

export function fetchCouponHistory(token: string) {
  return apiFetch<{ items?: CouponHistoryItem[] }>('/api/deposit/coupons/history', {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}

/** One movement on the ticket statement: signed, timestamped, balance-stamped. */
export type TicketLedgerEntry = {
  id?: number;
  kind?: 'earn' | 'spend' | 'refund';
  delta?: number;
  balanceAfter?: number;
  source?: string;
  sourceRef?: string;
  eventId?: string;
  eventScheduledAt?: number;
  eventStatus?: string;
  createdAt?: number;
};

export type TicketLedgerResponse = {
  balance?: number;
  entries?: TicketLedgerEntry[];
  wins?: { eventId?: string; prizeRank?: number; prizeAmount?: number; serialNo?: number; createdAt?: number }[];
};

export function fetchTicketLedger(token: string) {
  return apiFetch<TicketLedgerResponse>('/api/raffle/tickets/ledger', {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}

export function claimDepositCoupon(token: string, code: string) {
  return apiFetch<{ item?: DepositCoupon }>(`/api/deposit/coupons/claim/${encodeURIComponent(code)}`, {
    method: 'POST',
    headers: { ...clientHeaders(), ...authHeader(token) },
  });
}

export function createWalletWithdrawal(
  token: string,
  payload: { channelId: string; amount: number; destinationType: string; destinationRef: string; destinationName: string; destinationDocument?: string; abandonBonus?: boolean; quoteToken?: string; confirmedBonusAmount?: number },
) {
  return apiFetch<WalletOrderResponse>('/api/wallet/withdrawals', {
    method: 'POST',
    headers: {
      ...clientHeaders(),
      ...authHeader(token),
    },
    body: jsonBody(payload),
  });
}

export function fetchBonusAuditStatus(token: string) {
  return apiFetch<BonusAuditStatus>('/api/wallet/bonus-audit', {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}
export function fetchBonusAuditRecords(token: string) {
  return apiFetch<{items: BonusAuditRecord[]}>('/api/wallet/bonus-audit/records', { headers: { ...clientHeaders(), ...authHeader(token) }, cache: 'no-store' });
}
export function fetchWithdrawalEligibility(token: string, amount?: number) {
  const query = amount != null && amount > 0 ? `?amount=${encodeURIComponent(String(amount))}` : '';
  return apiFetch<WithdrawalEligibility>(`/api/wallet/withdrawal-eligibility${query}`, {
    headers: { ...clientHeaders(), ...authHeader(token) },
    cache: 'no-store',
  });
}
